import Image from 'next/image';
import { Box, Typography } from "@mui/material";
import BookingInfoRow from './BookingInfoRow';
import { formatDateDisplay, formatTimeDisplay } from '@/utils/utils';
import { useTourBookingContext } from '@/containers/TourBookingByCustomer/providers/useTourBookingContext';
import {
  TourDetailInfomationDetailSection,
  TourDetailBookingInfoTypeContainer,
  TourDetailBookingInfoChild,
  TourDetailBookingInfoTitle
} from './style';

import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { formatPriceWithNoUnit } from '@/utils/formatPrice';

export const BookingInfoHeader = () => {

  const { tourDetail } = useTourBookingContext();
  const theme = useTheme();

  return (
    <Box display="flex" mb={{ xs: 2, md: 3 }}>
      {tourDetail?.images?.[0] && (
        <Image src={tourDetail.images[0]} alt="tour-detail"
          width={theme.breakpoints.up('md') ? 80 : 60}
          height={theme.breakpoints.up('md') ? 64 : 48}
          style={{
            objectFit: 'cover',
            borderRadius: 8,
            flex: 1
          }} />
      )}
      <Box flex={3} ml={2}>
        <Typography variant="h2" fontWeight={700}>{tourDetail?.tourContents?.[0]?.title}</Typography>
        {/*<Typography variant="body1" color="text.secondary">{tourDetail?.tourType || '-'}</Typography>*/}
      </Box>
    </Box>
  );
}

const TitleBookingInformationDetail = ({ title, children, sx }: { title?: React.ReactNode, children: React.ReactNode, sx?: any }) => (
  <TourDetailBookingInfoTypeContainer sx={sx}>
    {title != undefined ? title : null}
    {children}
  </TourDetailBookingInfoTypeContainer>
)

const BookingInformationDetail = ({ haveBookingTitle }: { haveBookingTitle?: boolean }) => {

  const { t } = useTranslation();
  const { tourDetail, bookingTourData, contactInfo, durationOptions, computedPricing, services } = useTourBookingContext();

  const tourPriceOptionTitle = durationOptions?.find((option) => option.id === bookingTourData.tourPriceOptionId)?.label;

  // Compose additional services string
  const selectedServices: { name: string; price: number }[] = computedPricing?.additional_service_list?.map((selected_service) => {
    const foundService = services.find((service) => service.id === selected_service.id);
    return foundService ? { name: foundService.name, price: foundService.price } : null;
  })
    .filter((item): item is { name: string; price: number } => !!item) ?? [];

  const selectedTourPriceOption = tourDetail?.tourPriceOptions.find((option) => option.id === bookingTourData.tourPriceOptionId);
  const contactInfoRenderKey = JSON.stringify({
    fullName: contactInfo.fullName,
    email: contactInfo.email,
    phone: contactInfo.phone,
    zalo: contactInfo.zalo,
    whatsapp: contactInfo.whatsapp,
    kakaotalk: contactInfo.kakaotalk,
    viber: contactInfo.viber,
    dialCode: contactInfo.dialCode,
  });

  const selectedVisitPlaceList: { name: string; price: number }[] = computedPricing?.visit_place_list?.map((selected_visit_place) => {
    const foundVisitPlace = selectedTourPriceOption?.visitPlaces?.find((visit_place) => visit_place.id === selected_visit_place.id);
    return foundVisitPlace ? { name: foundVisitPlace?.contents?.at(0)?.name ?? '', price: selected_visit_place?.price } : null;
  })
    .filter((item): item is { name: string; price: number } => !!item) ?? [];



  return <TourDetailInfomationDetailSection>
    {/* Booking Information */}
    <TitleBookingInformationDetail title={haveBookingTitle ? <BookingInfoHeader /> : null}>
      <TourDetailBookingInfoChild >
        <BookingInfoRow key={'bookingDate'} label={t('bookingDate') + ':'} value={bookingTourData.date.format(formatDateDisplay)} />
        <BookingInfoRow key={'bookingTime'} label={t('bookingTime') + ':'} value={bookingTourData.time.format(formatTimeDisplay)} />
        <BookingInfoRow key={'tourDuration'} label={t('tourDuration') + ':'} value={tourPriceOptionTitle ?? ''} />
        <BookingInfoRow key={'adults'} label={t('adults') + ':'} value={`${bookingTourData.passengerNormal} ${t('passengers')}`} />
        <BookingInfoRow key={'pickupLocation'} label={t('pickupLocation') + ':'} value={bookingTourData.pickUpAddress} />
        {/*<BookingInfoRow label={t('additionalService') + ':'} value={servicesDisplay || '-'} />*/}
        <BookingInfoRow label={t('notes') + ':'} value={bookingTourData.note || '-'} alignTop />
      </TourDetailBookingInfoChild>
    </TitleBookingInformationDetail>

    {/* Booking Contact Info */}
    <TitleBookingInformationDetail
      key={contactInfoRenderKey}
      title={<TourDetailBookingInfoTitle>{t('passengerInfo')}</TourDetailBookingInfoTitle>}

    >
      <TourDetailBookingInfoChild>
        <BookingInfoRow label={t('fullName') + ':'} value={contactInfo.fullName} />
        <BookingInfoRow label='Email :' value={contactInfo.email} />
        {
          (contactInfo.phone && contactInfo.phone !== '') &&
          <BookingInfoRow label={t('phone') + ':'} value={contactInfo.phone} />
        }
        {
          (contactInfo.zalo && contactInfo.zalo !== '') &&
          <BookingInfoRow label='Zalo :' value={contactInfo.zalo} />
        }
        {
          (contactInfo.whatsapp && contactInfo.whatsapp !== '') &&
          <BookingInfoRow label='Whatsapp :' value={contactInfo.whatsapp} />
        }
        {
          (contactInfo.kakaotalk && contactInfo.kakaotalk !== '') &&
          <BookingInfoRow label='KakaoTalk :' value={contactInfo.kakaotalk} />
        }
        {
          (contactInfo.viber && contactInfo.viber !== '') &&
          <BookingInfoRow label='Viber :' value={contactInfo.viber} />
        }
      </TourDetailBookingInfoChild>
    </TitleBookingInformationDetail>

    {
      computedPricing != null ? (
        <>
          {/* Booking Additional Services */}
          {
            (computedPricing?.additional_service_list && computedPricing?.additional_service_list?.length > 0) ? (
              <TitleBookingInformationDetail
                title={<TourDetailBookingInfoTitle>{t('additionalServiceTitle')}</TourDetailBookingInfoTitle>}
                sx={{
                  borderBottom: computedPricing.visit_place_list.length === 0 ? 'unset' : undefined
                }}

              >
                {
                  selectedServices && selectedServices.length > 0 ?
                    (
                      <TourDetailBookingInfoChild>
                        {selectedServices.map((service, index) => (
                          <BookingInfoRow key={index} label={service.name + ':'} value={`${formatPriceWithNoUnit(service.price)} vnđ`} />
                        ))}
                      </TourDetailBookingInfoChild>
                    ) : null
                }
              </TitleBookingInformationDetail>
            )
              : null
          }


          {/* Booking Visit Places Fees */}
          {
            (computedPricing?.visit_place_list && computedPricing?.visit_place_list?.length > 0) ? (
              <TitleBookingInformationDetail
                title={<TourDetailBookingInfoTitle>{t('visit_places')}</TourDetailBookingInfoTitle>}

              >
                {
                  selectedVisitPlaceList && selectedVisitPlaceList.length > 0 ? (
                    <TourDetailBookingInfoChild sx={{ marginBottom: 0 }}>
                      {selectedVisitPlaceList.map((visit_place, index) => (
                        <BookingInfoRow key={index} label={visit_place.name + ':'} value={`${formatPriceWithNoUnit(visit_place.price)} vnđ`} />
                      ))}
                    </TourDetailBookingInfoChild>
                  ) : null
                }
              </TitleBookingInformationDetail>
            ) : null
          }
        </>
      ) : null
    }

  </TourDetailInfomationDetailSection>
}



export default BookingInformationDetail;
